import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  IconButton,
  Box,
  Typography,
  Modal,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import classes from "./MailDialog.module.scss";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getMessage, getSubject } from "../../../../constants/mailTemplates";
import { loadingState, loadingText } from "../../../../states/loadingState";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { accessTokenState } from "../../../../states/accessTokenState";
import { userState } from "../../../../states/userState";
import { User } from "../../../../types";
import DialogButton from "../../../uikit/atoms/buttons/DialogButton";
const sendEmail = async (accessToken: string, rawEmail: any) => {
  try {
    const response = await fetch(
      "https://gmail.googleapis.com/gmail/v1/users/me/messages/send",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          raw: rawEmail,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error("メール送信エラー:", errorData);
    } else {
      console.log("メールが正常に送信されました");
    }
  } catch (error) {
    console.error("メール送信中にエラーが発生しました:", error);
  }
};
type Props = {
  pdfName: string;
  pdfData: ArrayBuffer;
  pdfUrl: string;
  factoryId: number;
  wokerId: String;
  workerName: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  toEmail: string;
  ccEmail: string;
};
const MailDialog: React.FC<Props> = ({
  pdfName,
  pdfData,
  pdfUrl,
  factoryId,
  wokerId,
  workerName,
  open,
  setOpen,
  toEmail,
  ccEmail,
}) => {
  const setLoadingAnimation = useSetRecoilState(loadingState);
  const [loading, setLoading] = useState(false);
  const setLoadingText = useSetRecoilState(loadingText);
  const [emailsTo, setEmailsTo] = useState(toEmail);
  const [emailsCc, setEmailsCc] = useState(ccEmail);
  const [title, setTitle] = useState(getSubject(workerName));
  const [content, setContent] = useState(getMessage(workerName));
  const accessToken = useRecoilValue(accessTokenState);
  const user = useRecoilValue<User>(userState);
  const theme = useTheme();
  const isNotePc = useMediaQuery(theme.breakpoints.down("lg"));

  const createEmail = (
    to: string,
    cc: string,
    from: string,
    subject: string,
    message: string,
    pdfData: ArrayBuffer,
    pdfName: string
  ): string => {
    // Helper function to encode strings to Base64

    // Base64 encode the subject
    const encodedSubject = `=?utf-8?B?${btoa(
      unescape(encodeURIComponent(subject))
    )}?=`;
    // const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    //   const binary = String.fromCharCode(...new Uint8Array(buffer));
    //   return btoa(binary);
    // };
    const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
      const bytes = new Uint8Array(buffer);
      let binary = "";
      const chunkSize = 8192; // 一度に処理するチャンクサイズ
      for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode.apply(
          null,
          bytes.subarray(i, i + chunkSize) as unknown as number[]
        );
      }
      return btoa(binary);
    };

    // Email headers
    const headers = [
      `To: ${to}`,
      `From: ${from}`,
      `Subject: ${encodedSubject}`,
      "MIME-Version: 1.0",
      // 'Content-Type: text/plain; charset="UTF-8"',
      'Content-Type: multipart/mixed; boundary="boundary"',
    ];

    // Add Cc if present
    if (cc && cc.trim()) {
      headers.splice(1, 0, `Cc: ${cc}`);
    }

    // Email body
    // メール本文
    //   let emailBody = `
    // --boundary
    // Content-Type: text/plain; charset="UTF-8"

    // ${message}
    // `;
    const base64EncodedPdf = arrayBufferToBase64(pdfData);
    let emailBody = `
--boundary
Content-Type: text/plain; charset="UTF-8"

${message}

--boundary
Content-Type: application/pdf; name="${pdfName}"
Content-Disposition: attachment; filename="${pdfName}"
Content-Transfer-Encoding: base64

${base64EncodedPdf}

--boundary--
  `;
    // emailBody += "--boundary--";
    const email = [...headers, "", emailBody].join("\n");

    // Encode the entire email as Base64
    const base64EncodedEmail = btoa(unescape(encodeURIComponent(email)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");

    return base64EncodedEmail;
  };

  const handleSendEmail = async () => {
    if (!emailsTo) {
      window.alert("宛先が入力されていません");
      return;
    }
    if (!pdfData) {
      window.alert("添付ファイルが選択されていません");
      return;
    }
    if (!accessToken) {
      window.alert("アクセストークンが確認できません。再ログインしてください");
      return;
    }
    if (!user.mail) {
      window.alert("認証情報が確認できません。再ログインしてください");
      return;
    }

    try {
      const rawEmail = createEmail(
        emailsTo,
        emailsCc,
        user.mail,
        title,
        content,
        pdfData,
        pdfName
      );

      setLoadingAnimation(true);
      setLoadingText("送信中");
      setLoading(true);
      await sendEmail(accessToken, rawEmail);
      setLoadingAnimation(false);
      setLoadingText("");
      toast.info("送信しました!", {
        containerId: "MailForm",
        position: "top-left",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
      setOpen(false);
    } catch (error) {
      console.error("メール送信に失敗しました", error);
    } finally {
      setLoadingAnimation(false);
      setLoadingText("");
      setLoading(false);
    }
  };

  useEffect(() => {}, [pdfData]);
  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{ padding: 2 }} className={classes.formContainer}>
        {/* <Typography variant="h6" gutterBottom>
          Send Email
        </Typography> */}

        <TextField
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="To(「,」区切り)"
          rows={2}
          value={emailsTo}
          onChange={(e) => setEmailsTo(e.target.value)}
          required
          margin="normal"
        />
        <TextField
          InputLabelProps={{ shrink: true }}
          fullWidth
          label="CC(「,」区切り)"
          value={emailsCc}
          onChange={(e) => setEmailsCc(e.target.value)}
          margin="normal"
        />
        <TextField
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="件名"
          rows={2}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          margin="normal"
        />
        <TextField
          fullWidth
          label="本文"
          InputLabelProps={{ shrink: true }}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          multiline
          rows={isNotePc ? 4 : 8}
          margin="normal"
        />
        {/* <TextField
        fullWidth
        label="Signature"
        value={sign}
        onChange={(e) => setSign(e.target.value)}
        multiline
        rows={3}
        margin="normal"
      /> */}

        {/* PDF 表示リンク */}
        <Box sx={{ marginTop: 2 }} className={classes.attached}>
          <Typography className={classes.label}>添付ファイル:</Typography>
          <Typography>
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
              {pdfName}
            </a>
          </Typography>
        </Box>

        <Box sx={{ marginTop: 3, display: "flex", gap: 4 }}>
          <DialogButton
            label={loading ? "送信中.." : "送信する"}
            onClick={handleSendEmail}
            mode="yes"
            disabled={loading}
          />
          <DialogButton
            label={loading ? "送信中.." : "キャンセル"}
            onClick={() => setOpen(false)}
            mode="no"
            disabled={loading}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default MailDialog;
